import type { BundleSummaryDto, ClientShows, PipelineDto } from '@nsb/core';
import { useRef, useState } from 'react';
import { ApiError, errorText, queryClient, upload, useApi } from '../../api.ts';
import { Button, ErrorLine, Section, TextLink, Toggle } from '../../components/ui.tsx';
import { currentEnvironment } from '../../environment.ts';
import { dateWords } from '../../days.ts';

/**
 * Settings → Development tools → Copy a project to production (feature spec
 * "client page" §3): a project built on this machine, downloaded as a bundle
 * with Data on Local and loaded with Data on Production, dry run first.
 * Admins only.
 */

const SHOWN: [keyof ClientShows, string][] = [
  ['price', 'the price'],
  ['funnel', 'the funnel'],
  ['process', 'the process'],
];
const shownWords = (s: ClientShows) => SHOWN.filter(([k]) => s[k]).map(([, w]) => w).join(', ') || 'none of it';
const when = (iso: string) => dateWords(iso, 'moment');

export function BundlesSection() {
  const { data } = useApi<PipelineDto>(['pipeline', false], '/projects');
  const env = currentEnvironment();
  const projects = data ? [...data.projects, ...data.leads].filter((p) => !p.isSample && p.code) : [];
  return (
    <Section title="Copy a project to production">
      <p className="max-w-measure text-ink-2">
        Build a project here, then copy it over: its files, review iterations, history, and what its client sees. Download its bundle with Data on Local, switch Data to
        Production, and load it there. Loading adds only what is not there yet and changes nothing that is; it shows what it will add first.
      </p>
      <p className="meta mt-2">A bundle holds client documents: keep it on this machine, never in the repository.</p>

      <h3 className="label mt-6">Download a bundle{env === 'production' ? ' (from production)' : ''}</h3>
      {projects.length ? (
        <ul className="mt-2 divide-y divide-rule border-y border-rule">
          {projects.map((p) => (
            <li key={p.id} className="flex flex-wrap items-baseline justify-between gap-x-6 py-3">
              <span>
                {p.name} <span className="num meta">{p.code}</span>
              </span>
              <a href={`/api/projects/${p.id}/bundle`} download className="link text-sm text-ink-2 hover:text-ink">
                Download
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-ink-3">No projects with a code yet.</p>
      )}

      <h3 className="label mt-8">Load a bundle{env === 'local' ? ' (into this machine’s database)' : ''}</h3>
      <LoadBundle />
    </Section>
  );
}

function LoadBundle() {
  const picker = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [summary, setSummary] = useState<BundleSummaryDto | null>(null);
  // What the client sees changes only when ticked.
  const [settings, setSettings] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const send = async (f: File, dryRun: boolean) => {
    setBusy(true);
    setErr(null);
    try {
      const s = await upload<BundleSummaryDto>(`/bundles?dryRun=${dryRun}&settings=${settings}`, f, 'application/octet-stream');
      setSummary(s);
      if (!dryRun) await queryClient.invalidateQueries({ queryKey: ['project', s.project.id] });
    } catch (e) {
      setErr(e instanceof ApiError && e.status === 413 ? 'That bundle is too big to load at once (250 MB).' : errorText(e));
      setSummary(null);
    } finally {
      setBusy(false);
    }
  };
  const reset = () => {
    setFile(null);
    setSummary(null);
    setErr(null);
    if (picker.current) picker.current.value = '';
  };

  const s = summary;
  const count = s ? s.adds.files.length + s.adds.links.length + s.adds.iterations.length + s.adds.history.length + s.adds.board.length + (s.adds.patterns?.length ?? 0) : 0;
  return (
    <div className="mt-2">
      <input
        ref={picker}
        type="file"
        accept=".nsb-bundle,application/octet-stream"
        className="sr-only"
        aria-label="Choose a bundle"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          setFile(f);
          void send(f, true);
        }}
      />
      {!file ? (
        <Button tone="secondary" onClick={() => picker.current?.click()}>
          Choose a bundle
        </Button>
      ) : null}
      {busy && !s ? <p className="meta mt-2 motion-safe:animate-pulse">Reading {file?.name}</p> : null}
      {s ? (
        <div className="mt-3 max-w-2xl border border-rule bg-sheet p-4 sm:p-5">
          <p className="label">{s.loaded ? 'Loaded' : 'Loading it would'}</p>
          <p className="mt-2">
            {s.loaded ? 'Added to' : 'Add to'} <TextLink to={`/p/${s.project.id}`}>{s.project.name}</TextLink>{' '}
            <span className="num meta">{s.project.code}</span>, from {s.from.env === 'development' ? 'development' : s.from.env}, made{' '}
            <span className="num">{when(s.from.createdAt)}</span>:
          </p>
          {s.warnings.length ? (
            <ul className="mt-2 space-y-1 text-sm text-alert">
              {s.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          ) : null}
          {count === 0 ? <p className="mt-2 text-ink-2">Nothing: all of it is here already.</p> : null}
          <ul className="mt-2 space-y-1 text-sm text-ink-2">
            {(
              [
                ['file', 'files', s.adds.files],
                ['link', 'links', s.adds.links],
                ['review iteration', 'review iterations', s.adds.iterations],
                ['history entry', 'history entries', s.adds.history],
                ['thing on the design board', 'things on the design board', s.adds.board],
                ['pattern on the map of patterns', 'patterns on the map of patterns', s.adds.patterns ?? []],
              ] as const
            )
              .filter(([, , list]) => list.length)
              .map(([one, many, list]) => (
                <li key={many}>
                  <span className="num">{list.length}</span> {list.length === 1 ? one : many}: {list.join('; ')}
                </li>
              ))}
          </ul>
          {s.alreadyHere ? (
            <p className="meta mt-2">
              <span className="num">{s.alreadyHere}</span> already here, left as they are.
            </p>
          ) : null}
          {s.clientSees.length ? (
            <div className="mt-4">
              <p className="label !text-cedar">The client {s.loaded ? 'now sees' : 'would newly see'}</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm">
                {s.clientSees.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {s.settings && !s.loaded ? (
            <div className="mt-4">
              <Toggle
                checked={settings}
                onChange={setSettings}
                label={`Show the client what the bundle shows: ${shownWords(s.settings.bundle)}`}
                hint={`Here they see ${shownWords(s.settings.now)}.`}
              />
            </div>
          ) : null}
          <div className="mt-4 flex flex-wrap gap-x-5">
            {!s.loaded && count + (s.settings && settings ? 1 : 0) > 0 ? (
              <Button busy={busy} onClick={() => file && void send(file, false)}>
                Load it
              </Button>
            ) : null}
            <Button tone="quiet" onClick={reset}>
              {s.loaded ? 'Done' : 'Cancel'}
            </Button>
          </div>
        </div>
      ) : null}
      <ErrorLine error={err} />
      {err && file ? (
        <Button tone="quiet" onClick={reset}>
          Choose another
        </Button>
      ) : null}
    </div>
  );
}
